// In src/components/WorkflowViewer.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  ReactFlowProvider
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button, Tooltip } from 'antd';
import { 
  ZoomInOutlined, 
  ZoomOutOutlined, 
  FullscreenOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import ViewerSidebar from './ViewerSidebar';
import { nodeTypes } from './nodes/nodeTypes';
import CustomEdge from './CustomEdge';

const createAnnotationViewerNode = (NodeComponent) => {
  return (props) => {
    // Create no-op handlers for onChange and onDelete
    const onChange = () => {};
    const onDelete = () => {};
    
    const viewerProps = {
      ...props,
      selected: false, // Don't show them as selected
      data: {
        ...props.data,
        onChange: onChange,
        onDelete: onDelete
      }
    };
    
    return <NodeComponent {...viewerProps} />;
  };
};

// Create a higher-order component to make nodes read-only or interactive
const createViewerNode = (NodeComponent, nodeType) => {
  // Handle annotation nodes differently
  if (['stickyNote', 'textAnnotation', 'shape'].includes(nodeType)) {
    return createAnnotationViewerNode(NodeComponent);
  }
  
  // Original handling for workflow nodes
  return (props) => {
    const dummyUpdateNodeData = () => {};
    const dummyHandleSettingChange = () => {};
    const dummyHandleContentChange = () => {};
    const dummyHandleModelChange = () => {};
    
    const onDelete = props.data?.isInteractive ? props.data.onDelete : () => {};
    
    const viewerProps = {
      ...props,
      isConnectable: props.isConnectable || false,
      data: {
        ...props.data,
        updateNodeData: dummyUpdateNodeData,
        onDelete: onDelete,
        handleSettingChange: dummyHandleSettingChange,
        handleContentChange: dummyHandleContentChange,
        handleModelChange: dummyHandleModelChange,
        readOnly: !props.data?.isInteractive
      }
    };
    
    return <NodeComponent {...viewerProps} />;
  };
};

// Update how viewerNodeTypes is created
const viewerNodeTypes = Object.fromEntries(
  Object.entries(nodeTypes).map(([key, component]) => [
    key,
    createViewerNode(component, key) // Pass the node type key
  ])
);

// Mobile detection utility - should match the PHP/JS detection
const isMobileDevice = () => {
  // Check if we already have the result from the parent script
  if (typeof window.wpAiWorkflowsMobile !== 'undefined') {
    return window.wpAiWorkflowsMobile;
  }
  
  // If not, calculate it
  const isMobile = window.innerWidth < 768 || 
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Save for future use
  window.wpAiWorkflowsMobile = isMobile;
  
  return isMobile;
};

const WorkflowViewerInner = (props) => {
  const {
    workflowData,
    height = '500px',
    darkMode = false,
    showMinimap = true,
    showControls = true,
    nodesDraggable = false,
    nodesConnectable = false,
    elementsSelectable = false,
    showSidebar = false,
    allowPanZoom = true,
    showToolbar = false,
    allowDownload = false,
    showOverlay = true,
    overlayText = "Click to Explore Workflow",
    hideOnMobile = false,
    hidePoweredBy = false
  } = props;

  // Define all state hooks at the top - always called in the same order
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [colorMode, setColorMode] = useState(darkMode ? 'dark' : 'light');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(isMobileDevice());
  const [overlayActive, setOverlayActive] = useState(true);
  const [shouldRender, setShouldRender] = useState(!isMobile || !hideOnMobile);
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  
  // Define all callbacks at the top level - never conditionally
  const onEdgeDelete = useCallback(
    (edgeId) => {
      if (elementsSelectable) {
        setEdges(edges => edges.filter(e => e.id !== edgeId));
      }
    },
    [elementsSelectable, setEdges]
  );
  
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      if (!nodesConnectable || !reactFlowInstance) return;

      event.preventDefault();
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode = {
        id: `${type}-${Date.now()}`,
        type,
        position,
        draggable: nodesDraggable,
        selectable: elementsSelectable,
        data: { 
          readOnly: !elementsSelectable,
          isViewer: true,
          isInteractive: elementsSelectable,
          onDelete: (id) => setNodes(nodes => nodes.filter(n => n.id !== id))
        }
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [nodesConnectable, reactFlowInstance, nodesDraggable, elementsSelectable, setNodes]
  );

  const onConnect = useCallback(
    (params) => nodesConnectable && setEdges((eds) => addEdge(params, eds)),
    [nodesConnectable, setEdges]
  );
  
  const onDragStart = useCallback((event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDownloadWorkflow = useCallback(() => {
    try {
      // Use the workflowData directly from props
      const data = workflowData;
      
      // Create a sanitized version of the workflow for download
      const downloadData = {
        name: data?.name || 'Exported Workflow',
        nodes: nodes.map(node => {
          // Create a simplified copy without functions or runtime data
          const { data, ...rest } = node;
          const simplifiedData = { ...data };
          
          // Remove function references and runtime properties
          ['updateNodeData', 'onDelete', 'onChange', 'handleSettingChange', 
           'handleContentChange', 'handleModelChange', 'isViewer', 'isInteractive', 
           'output', 'displayOutput', 'isExecuting', 'executed', 'executionError'].forEach(key => {
            delete simplifiedData[key];
          });
          
          return {
            ...rest,
            data: simplifiedData
          };
        }),
        edges: edges.map(({ source, target, sourceHandle, targetHandle, id, type }) => ({
          source, target, sourceHandle, targetHandle, id, type
        })),
        exportedAt: new Date().toISOString(),
        exportedFrom: 'workflow-viewer'
      };
      
      
      // Create and trigger download
      const blob = new Blob([JSON.stringify(downloadData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${downloadData.name.replace(/\s+/g, '-').toLowerCase()}-${new Date().getTime()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading workflow:', error);
    }
  }, [workflowData, nodes, edges]);

  // All effect hooks - always called in the same order
  // Track window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobileCheck = window.innerWidth < 768;
      setIsMobile(mobileCheck);
      setShouldRender(!mobileCheck || !hideOnMobile);
      // Also update global flag
      window.wpAiWorkflowsMobile = mobileCheck;
    };
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [hideOnMobile]);

  // Color mode effect
  useEffect(() => {
    setColorMode(darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Workflow data loading effect
  useEffect(() => {
    // Early return if we shouldn't show this
    if (!shouldRender) {
      return;
    }
    
    try {
      const parsedData = typeof workflowData === 'string' 
        ? JSON.parse(workflowData) 
        : workflowData;
      
      if (!parsedData || !parsedData.nodes || !parsedData.edges) {
        throw new Error('Invalid workflow data format');
      }
      
      const viewerNodes = parsedData.nodes.map(node => ({
        ...node,
        draggable: nodesDraggable,
        selectable: elementsSelectable,
        data: {
          ...node.data,
          readOnly: !elementsSelectable,
          isViewer: true,
          isInteractive: elementsSelectable,
          onDelete: (id) => setNodes(nodes => nodes.filter(n => n.id !== id))
        }
      }));
      
      const viewerEdges = parsedData.edges.map(edge => ({
        ...edge,
        data: { 
          ...edge.data,
          colorMode: colorMode,
          readOnly: !elementsSelectable,
          onDelete: elementsSelectable ? (id) => onEdgeDelete(id) : () => {}
        }
      }));
      
      setNodes(viewerNodes);
      setEdges(viewerEdges);
      setLoading(false);
    } catch (error) {
      console.error('Error initializing workflow viewer:', error);
      setError(`Failed to load workflow data: ${error.message}`);
      setLoading(false);
    }
  }, [workflowData, colorMode, nodesDraggable, elementsSelectable, setNodes, setEdges, shouldRender, onEdgeDelete]);

  // Early return if we shouldn't show on mobile - this comes after all hooks are defined
  if (!shouldRender) {
    return null; // Return nothing as the mobile message is handled by PHP
  }

  if (loading) {
    return (
      <div className="workflow-viewer-loading">
        <div className="loading-spinner"></div>
        <div className="loading-text">Loading workflow preview...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="workflow-viewer-error">
        <div className="error-icon">⚠️</div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {showSidebar && (
        <ViewerSidebar 
          darkMode={colorMode === 'dark'} 
          onDragStart={onDragStart}
        />
      )}
      
      <div 
        ref={reactFlowWrapper} 
        className={`workflow-viewer-container ${colorMode === 'dark' ? 'dark-mode' : 'light-mode'}`}
        style={{ height, flex: 1, position: 'relative' }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onInit={setReactFlowInstance}
          nodeTypes={viewerNodeTypes}
          edgeTypes={{
            default: CustomEdge,
          }}
          colorMode={colorMode}
          nodesDraggable={nodesDraggable}
          nodesConnectable={nodesConnectable}
          elementsSelectable={elementsSelectable}
          zoomOnScroll={allowPanZoom}
          panOnScroll={allowPanZoom}
          panOnDrag={allowPanZoom}
          onConnect={onConnect}
          onDrop={onDrop}
          onDragOver={onDragOver}
          defaultEdgeOptions={{ 
            type: 'default',
            animated: false
          }}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.1} 
          maxZoom={4} 
        >
          {showControls && <Controls showInteractive={allowPanZoom} />}
          {showMinimap && <MiniMap pannable={allowPanZoom} />}
          <Background variant="dots" gap={12} size={1} />
          
          {/* Main toolbar */}
          {showToolbar && (
            <div className="workflow-viewer-toolbar" style={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 10,
              display: 'flex',
              gap: '8px',
              background: colorMode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.8)',
              padding: '6px',
              borderRadius: '4px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
            }}>
              {reactFlowInstance && (
                <>
                  <Button size="small" icon={<ZoomInOutlined />} onClick={() => reactFlowInstance.zoomIn()} />
                  <Button size="small" icon={<ZoomOutOutlined />} onClick={() => reactFlowInstance.zoomOut()} />
                  <Button size="small" icon={<FullscreenOutlined />} onClick={() => reactFlowInstance.fitView()} />
                </>
              )}
              
              {allowDownload && (
                <Button 
                  size="small"
                  type="primary"
                  onClick={handleDownloadWorkflow}
                  icon={<DownloadOutlined />}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  Download
                </Button>
              )}
            </div>
          )}
          
          {/* Powered by text - only show if not hidden */}
          {!hidePoweredBy && (
            <div 
              style={{
                position: 'absolute',
                bottom: 10,
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: '12px',
                fontFamily: 'sans-serif',
                zIndex: 5
              }}
            >
              <a 
                href="https://wpaiworkflowautomation.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: colorMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = colorMode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = colorMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)';
                }}
              >
                Powered by AI Workflow Automation
              </a>
            </div>
          )}
        </ReactFlow>
      </div>
      
      {overlayActive && showOverlay && (
        <div 
          className="workflow-viewer-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            cursor: 'pointer'
          }}
          onClick={() => setOverlayActive(false)}
        >
          <Button
            type="primary"
            size="large"
            style={{
              fontSize: '16px',
              padding: '12px 24px',
              height: 'auto',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}
            onClick={() => setOverlayActive(false)}
          >
            {overlayText}
          </Button>
        </div>
      )}
    </div>
  );
};

const WorkflowViewer = (props) => (
  <ReactFlowProvider>
    <WorkflowViewerInner {...props} />
  </ReactFlowProvider>
);

export default WorkflowViewer;